import { css } from "styled-system/css";
import { ed, Masthead } from "~/components/editorial";
import type { Period } from "~/lib/period";
import { useTeamRead } from "../queries";

/** The Teams masthead with the period on the right. */
export function TeamsMasthead({ periodLabel, prevLabel }: { periodLabel: string; prevLabel: string }) {
  return (
    <Masthead
      label="Teams"
      meta={
        <>
          {periodLabel} vs. {prevLabel}
        </>
      }
    />
  );
}

/** A table cell value with last period struck through before it. */
export function Was({ prior, current }: { prior: string; current: string }) {
  return (
    <>
      <s className={styles.prior} aria-label={`was ${prior}`}>
        {prior}
      </s>
      {current}
    </>
  );
}

/**
 * The written read: a few paragraphs from Claude on how the teams moved
 * against the org, with the pages it cited. Says it's working while the
 * first read of a period is written; hidden when there's nothing to say.
 */
export function TeamReadSection({ range, teamId = null }: { range: Period; teamId?: string | null }) {
  const { data, isLoading } = useTeamRead(range, teamId);
  if (isLoading) {
    return (
      <section className={styles.read} aria-busy="true">
        <div className={ed.sect}>The read</div>
        <p className={ed.method}>Reading the numbers against published benchmarks…</p>
      </section>
    );
  }
  if (!data || data.paragraphs.length === 0) return null;
  return (
    <section className={styles.read}>
      <div className={ed.sect}>The read</div>
      {data.paragraphs.map((p, i) => (
        <p key={i} className={ed.prose}>
          {p}
        </p>
      ))}
      {data.sources.length > 0 && (
        <ol className={styles.sources}>
          {data.sources.map((s) => (
            <li key={s.url}>
              <a href={s.url} target="_blank" rel="noreferrer">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      )}
      <p className={ed.method}>
        {data.model
          ? "Written by Claude from the numbers on this page, with industry context from the sources listed. The headline above is templated from the numbers, not written by a model."
          : "Built from the numbers on this page. Industry context appears when the model is available."}
      </p>
    </section>
  );
}

const styles = {
  read: css({ mt: "28px" }),
  sources: css({
    listStyle: "decimal",
    pl: "20px",
    mt: "8px",
    fontFamily: "sans",
    fontSize: "12.5px",
    color: "ed.ink3",
    "& a": { color: "inherit", textDecoration: "underline" },
  }),
  prior: css({
    mr: "6px",
    fontSize: "10.5px",
    color: "ed.ink3",
    textDecorationThickness: "1px",
  }),
};
